import { useState, useCallback, useRef, useEffect } from 'react';
import {
  fetchBinancePrice,
  formatRateForForm,
  isStablecoin,
  FALLBACK_REFERENCE_RATES,
} from '../lib/rates.js';

export type FiatRatesUsd = { date: string; usd: Record<string, number> };

interface UseReferenceRateArgs {
  clientGivesCurrency: string;
  getCryptoCurrency: () => string;
  getFiatRatesUsd: () => Promise<FiatRatesUsd>;
  setRateValue: (value: string) => void;
  onError: (message: string) => void;
  /** Auto-refresh interval in milliseconds. Default: 15000 (15 seconds). Set to 0 to disable. */
  refreshInterval?: number;
}

// Default: ~0.87 EUR per 1 USDT (fiat-per-crypto direction, matching form computedRate)
const DEFAULT_REFERENCE_RATE = 1 / 1.085;
const DEFAULT_REFRESH_INTERVAL = 15000; // 15 seconds

/**
 * Hook to fetch and hold reference rate in **fiat-per-crypto** direction
 * (e.g. EUR/USDT ≈ 0.87, meaning 1 USDT costs 0.87 EUR).
 *
 * This matches the form's `computedRate = totalFiat / totalCrypto`.
 * Display code then shows "1 € = 1/referenceRate USDT" for the inverted view.
 *
 * Sources: fiat rates from currency-api, crypto prices from Binance.
 */
export function useReferenceRate({
  clientGivesCurrency,
  getCryptoCurrency,
  getFiatRatesUsd,
  setRateValue,
  onError,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
}: UseReferenceRateArgs) {
  const [referenceRate, setReferenceRate] = useState(DEFAULT_REFERENCE_RATE);
  const [rateSource, setRateSource] = useState<string>('');
  const [rateFetchedAt, setRateFetchedAt] = useState<Date | null>(null);
  const [isLoadingRate, setIsLoadingRate] = useState(false);
  /** When from Binance: raw price (USDT per 1 crypto) and crypto symbol for display e.g. "1 BTC = 71,736 USDT" */
  const [binancePriceUsdt, setBinancePriceUsdt] = useState<number | null>(null);
  const [binanceCryptoSymbol, setBinanceCryptoSymbol] = useState<string>('');

  const clientGivesCurrencyRef = useRef(clientGivesCurrency);
  const getCryptoCurrencyRef = useRef(getCryptoCurrency);
  const getFiatRatesUsdRef = useRef(getFiatRatesUsd);
  const setRateValueRef = useRef(setRateValue);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    clientGivesCurrencyRef.current = clientGivesCurrency;
    getCryptoCurrencyRef.current = getCryptoCurrency;
    getFiatRatesUsdRef.current = getFiatRatesUsd;
    setRateValueRef.current = setRateValue;
    onErrorRef.current = onError;
  }, [clientGivesCurrency, getCryptoCurrency, getFiatRatesUsd, setRateValue, onError]);

  const fetchReferenceRate = useCallback(async () => {
    setIsLoadingRate(true);
    try {
      const giveCurrency = (clientGivesCurrencyRef.current || 'EUR').toUpperCase();
      const getsCurrency = getCryptoCurrencyRef.current().toUpperCase();

      const { usd } = await getFiatRatesUsdRef.current();
      const currencyKey = giveCurrency.toLowerCase();
      const usdPerFiat = usd?.[currencyKey];
      const fiatInUsd =
        giveCurrency === 'USD'
          ? 1.0
          : typeof usdPerFiat === 'number' && usdPerFiat > 0
            ? 1 / usdPerFiat
            : (FALLBACK_REFERENCE_RATES[giveCurrency] ?? FALLBACK_REFERENCE_RATES['EUR']);

    if (isStablecoin(getsCurrency)) {
      // Stablecoin: 1 USDT ≈ 1 USD, so rate = fiat-per-stablecoin
      // fiatInUsd = USD value of 1 fiat unit (e.g. 1 EUR = 1.15 USD)
      // We want EUR/USDT = 1/fiatInUsd ≈ 0.87 (costs 0.87 EUR to buy 1 USDT)
      const rate = 1 / fiatInUsd;
      setBinancePriceUsdt(null);
      setBinanceCryptoSymbol('');
      setReferenceRate(rate);
      setRateValueRef.current(formatRateForForm(rate));
      setRateSource('currency-api');
      setRateFetchedAt(new Date());
      return;
    }

      const symbol = `${getsCurrency}USDT`;
      const priceUsdt = await fetchBinancePrice(symbol);
      // rate = fiat-per-crypto = priceUsdt / fiatInUsd
      // e.g. 1 BTC = 71000 USDT, 1 EUR = 1.15 USD → 1 BTC = 71000/1.15 ≈ 61739 EUR
      const rate = priceUsdt / fiatInUsd;
      setReferenceRate(rate);
      setRateValueRef.current(formatRateForForm(rate));
      setRateSource('Binance');
      setRateFetchedAt(new Date());
      setBinancePriceUsdt(priceUsdt);
      setBinanceCryptoSymbol(getsCurrency);
    } catch {
      setBinancePriceUsdt(null);
      setBinanceCryptoSymbol('');
      onErrorRef.current('Could not fetch crypto rate from Binance. Check connection or try another currency.');
    } finally {
      setIsLoadingRate(false);
    }
  }, []);

  // Auto-refresh rate on interval
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      fetchReferenceRate();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [fetchReferenceRate, refreshInterval]);

  return {
    referenceRate,
    setReferenceRate,
    fetchReferenceRate,
    rateSource,
    rateFetchedAt,
    isLoadingRate,
    binancePriceUsdt,
    binanceCryptoSymbol,
  };
}
